import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Mail, RefreshCw, Sparkles } from 'lucide-react';
import { useWorkspaceCredits } from '../../contexts/CreditsContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { formatDateTime, formatNumber } from '../../lib/i18n';

const primaryCtaLabelByPlan = (planCode, t) => {
  if (planCode === 'FREE') return t('navCredits.actions.upgradePlan');
  return t('navCredits.actions.buyMoreCredits');
};

function CreditBucketRow({ bucket }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="rounded-xl border border-border/60 bg-muted/[0.18] px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-black text-foreground tracking-tight">{bucket.label}</div>
          <div className="text-[10px] text-muted-foreground font-medium mt-0.5 leading-snug">
            {bucket.description}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-black text-foreground">{formatNumber(bucket.remaining, {}, i18n.language)}</div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{t('navCredits.remaining')}</div>
        </div>
      </div>
      <div className="mt-2 text-[10px] font-medium text-muted-foreground">
        {t('navCredits.usedSummary', {
          used: formatNumber(bucket.used, {}, i18n.language),
          total: formatNumber(bucket.total || bucket.base + bucket.topUp, {}, i18n.language),
        })}
      </div>
    </div>
  );
}

export default function NavCreditsHub() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    commercialState,
    creditBuckets,
    otherCreditBuckets,
    primaryEmailCredits,
    isLoading,
    status,
    planCode,
    lastSyncedAt,
    refreshCredits
  } = useWorkspaceCredits();

  const hasLoadedCredits = !!commercialState || !!lastSyncedAt;
  const emailBucket = creditBuckets.find((bucket) => bucket.key === 'emailUnlocksCount');
  const isLowEmailCredits = hasLoadedCredits && (emailBucket?.remaining ?? primaryEmailCredits) <= 20;

  const handleOpenBilling = () => navigate('/plan');

  return (
    <TooltipProvider delayDuration={250}>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  'h-10 rounded-xl border px-2.5 sm:px-3.5 transition-all',
                  'border-primary/25 bg-primary/[0.08] hover:bg-primary/[0.14] hover:border-primary/35',
                  'focus-visible:ring-1 focus-visible:ring-primary/30'
                )}
              >
                <div className="flex items-center gap-2 sm:gap-2.5">
                  <div className="h-6 w-6 rounded-lg border border-primary/25 bg-background/90 flex items-center justify-center">
                    <Mail className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="flex flex-col items-start leading-none">
                    <span className="hidden lg:block text-[9px] font-black uppercase tracking-[0.18em] text-primary/70">
                      {t('navCredits.emailCredits')}
                    </span>
                    <span className="text-xs sm:text-sm font-black tracking-tight text-foreground">
                      {isLoading && !hasLoadedCredits ? '...' : hasLoadedCredits ? formatNumber(primaryEmailCredits, {}, i18n.language) : '—'}
                    </span>
                  </div>
                </div>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent className="text-xs font-medium">
            {t('navCredits.currentBalance')}
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          align="end"
          sideOffset={10}
          className="w-[min(380px,calc(100vw-1.25rem))] p-0 rounded-2xl border border-border/70 bg-popover/95 backdrop-blur-xl shadow-2xl"
        >
          <div className="px-4 py-4 bg-gradient-to-br from-primary/[0.14] via-background to-background rounded-t-2xl border-b border-border/60">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-black tracking-tight text-popover-foreground">{t('navCredits.title')}</h3>
                </div>
                <p className="text-[11px] text-muted-foreground font-medium mt-1 leading-snug max-w-[260px]">
                  {t('navCredits.description')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {status !== 'ACTIVE' && (
                  <Badge variant="warning" className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5">
                    {status}
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-lg border border-border/60 bg-background/80"
                  onClick={() => refreshCredits({ silent: true })}
                >
                  <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="sr-only">{t('navCredits.actions.refreshCredits')}</span>
                </Button>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-primary/20 bg-card/80 px-3.5 py-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-primary/75">{t('navCredits.emailCredits')}</div>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                    {t('navCredits.emailCreditsDescription')}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-black text-foreground leading-none">
                    {hasLoadedCredits ? formatNumber(primaryEmailCredits, {}, i18n.language) : '—'}
                  </div>
                  <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mt-1">{t('navCredits.remaining')}</div>
                </div>
              </div>

              {emailBucket && (
                <div className="mt-3 flex items-center justify-between text-[10px] font-medium text-muted-foreground">
                  <span>{t('navCredits.usedOnlySummary', {
                    used: formatNumber(emailBucket.used, {}, i18n.language),
                    total: formatNumber(emailBucket.total || (emailBucket.base + emailBucket.topUp), {}, i18n.language),
                  })}</span>
                  {isLowEmailCredits && (
                    <Badge variant="destructive" className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5">
                      {t('navCredits.low')}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="px-4 py-3.5 space-y-3.5">
            <div className="space-y-2.5">
              {otherCreditBuckets.length > 0 ? (
                otherCreditBuckets.map((bucket) => (
                  <CreditBucketRow key={bucket.key} bucket={bucket} />
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-border/70 bg-muted/[0.14] px-3 py-3 text-[11px] font-medium text-muted-foreground">
                  {t('navCredits.noSecondaryBuckets')}
                </div>
              )}
            </div>

            <Separator className="bg-border/70" />

            <div className="flex items-center gap-2">
              <Button className="flex-1 h-9 rounded-xl font-black text-[11px] tracking-wide" onClick={handleOpenBilling}>
                {primaryCtaLabelByPlan(planCode, t)}
                <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
              </Button>
              <Button
                variant="outline"
                className="h-9 rounded-xl border-border/60 text-[11px] font-bold"
                onClick={handleOpenBilling}
              >
                {t('navCredits.actions.manageBilling')}
              </Button>
            </div>

            <div className="text-[10px] font-medium text-muted-foreground">
              {lastSyncedAt
                ? t('navCredits.lastSyncedAt', { time: formatDateTime(lastSyncedAt, { timeStyle: 'short' }, i18n.language) })
                : t('navCredits.syncHint')}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}
